import {Ticket} from "Stores/dashboardStore/tickets";
import {Utils} from "../../../common/utils";
import {categories, viewBys} from "./Controls";

const minDayRange = 30;
const msInDay = 86400000;

interface DateRange {
    start: Date
    stop: Date
}

export interface Counts {
    "grand-total": number
    "open-late": number
    "closed-not-met": number
    "open-on-time": number
    "closed-met": number
    "no-date": number
}

export interface EntityCounts extends Counts {
    name: string
}

export interface FilterChanges {
    viewBy: viewBys,
    viewByFilters?: Array<string>
    labelGroupFilters?: Array<string>
    labelFilters?: Array<string>
    keywords?: string
}

export interface ActionplanState {
    viewBy: viewBys

    minDate?: Date
    maxDate?: Date
    dateRange?: DateRange

    // This is the earliest created dated/due date from the actionplans that are included.
    minFilteredDate?: Date
    // The is the latest due date from the actionplans that are included.
    maxFilteredDate?: Date

    allActionplans: Array<Ticket>

    // This is a subset of actionplans.  Useful for contextual filter selections as well.
    filteredActionplans: Array<Ticket>

    // This is the number of total actionplans per status without status filtering
    counts: Counts

    // This is the number of total actionplans per status per assignee|committee|w/e
    viewByCounts: Map<string, EntityCounts>

    // This is the same as above, just sorted
    sortedViewByCounts: Array<EntityCounts>

    // These are in the filter dropdown menu
    viewByFilters?: Array<string>
    labelGroupFilters?: Array<string>
    labelFilters?: Array<string>
    keywords?: string

    // This is when you click on an entity in the selected view.
    // If viewing by assignee, this would be one specific assignee
    focusedViewBy?: string
    focusedActionplan?: Ticket

    // This is when you click on a bar chart rectangle.  It is the category
    focusedCategory?: string

    // Sorting by count of a status.
    sortCategory?: string

    // Sort direction, 0 Desc, 1 ascending, null back to default sort (which is the viewBy)
    sortDirection: number

    // This is used to render the same scale on all graphs
    maxCount: number

    includeAllStatus: boolean
    includeOpenLate: boolean
    includeOpenOnTime: boolean
    includeClosedMet: boolean
    includeClosedNotMet: boolean
    includeNoDate: boolean
}

// These all return arrays. Labels and Label Groups may be plural for an actionplan.
export const viewByAccessors = {
    assignee_name: (a) => {return [a?.assignee_name || "Not Assigned"];},
    dashboard: (a) => {return [a?.dashboard || "No Dashboard"];},
    creator: (a) => {return [a.created_by_name || "No Creator"];},
    committee: (a) => {return [a?.committee_name || "No Committee"];},
    course: (a) => {return [a?.course || "No Course"];},
    label_group: actionplanLabelGroups,
    label: actionplanLabels,
};

export function newCountMap(): Counts {
    return {
        "grand-total": 0,
        "open-late": 0,
        "closed-not-met": 0,
        "open-on-time": 0,
        "closed-met": 0,
        "no-date": 0
    };
}

export function getViewBys(state: ActionplanState) {
    const viewByValues: Array<string> = [];
    for (const actionplan of state.allActionplans) {
        for (const viewByKey of viewByAccessors[state.viewBy](actionplan)) {
            if (viewByValues.indexOf(viewByKey) === -1) {
                viewByValues.push(viewByKey);
            }
        }
    }
    return viewByValues;
}

function actionplanLabelGroups(actionplan: Ticket) {
    const groups: Array<string> = [];
    for (const key in actionplan.labels) {
        for (const label of actionplan.labels[key]) {
            if (groups.indexOf(label.label_group.name) === -1) {
                groups.push(label.label_group.name);
            }
        }
    }
    if (groups.length === 0) { groups.push("Unlabeled"); }
    return groups;
}

function actionplanLabels(actionplan: Ticket) {
    const labels: Array<string> = [];
    for (const key in actionplan.labels) {
        for (const label of actionplan.labels[key]) {
            if (labels.indexOf(label.name) === -1) {
                labels.push(label.name);
            }
        }
    }
    if (labels.length === 0) { labels.push("Unlabeled"); }
    return labels;
}

export function getLabelGroups(state: ActionplanState) {
    // Make unique set of label group names
    const labelGroups: {[key: string]: boolean} = {};
    for (const actionplan of state.allActionplans) {
        for (const group of actionplanLabelGroups(actionplan)) {
            labelGroups[group] = true;
        }
    }

    // Return them as an array.
    const labelGroupsArray = new Array<string>();
    for (const labelName in labelGroups) {
        labelGroupsArray.push(labelName);
    }
    labelGroupsArray.sort();
    return labelGroupsArray;
}

export function getLabels(state: ActionplanState) {
    const labels: {[key: string]: boolean} = {};
    for (const actionplan of state.allActionplans) {
        for (const label of actionplanLabels(actionplan)) {
            labels[label] = true;
        }
    }

    // Return them as an array
    const labelArray = new Array<string>();
    for (const label in labels) {
        labelArray.push(label);
    }
    labelArray.sort();
    return labelArray;
}

function isFilteredByDateRange(actionplan: Ticket, dateRange?: DateRange) {
    const parsedDate = actionplan.evaluation_date ?
        new Date(actionplan.evaluation_date as string) :
        undefined;

    let result = false;
    if (dateRange !== undefined &&
        parsedDate !== undefined &&
        (
            parsedDate < dateRange.start ||
            parsedDate > dateRange.stop
        )
    ) {
        result = true;
    }
    return result;
}

function isFilteredByViewByFilters(state: ActionplanState, actionplan: Ticket) {
    if (state.viewByFilters) {
        for (const value of viewByAccessors[state.viewBy](actionplan)) {
            // If we find a match, let it be included
            if (state.viewByFilters.indexOf(value) !== -1) {
                return false;
            }
        }
        // No matches, this actionplan is filtered.
        return true;
    }
    return false;
}

function isFilteredByLabelGroups(state: ActionplanState, actionplan: Ticket) {
    if (state.labelGroupFilters) {
        for (const value of viewByAccessors[viewBys.LABELGROUP](actionplan)) {
            // if we find a match, let it be included.
            if (state.labelGroupFilters.indexOf(value) !== -1) {
                return false;
            }
        }
        // No matches, this actionplan is filtered
        return true;
    }
    return false;
}

function isFilteredByLabels(state: ActionplanState, actionplan: Ticket) {
    if (state.labelFilters) {
        for (const value of viewByAccessors[viewBys.LABEL](actionplan)) {
            // if we find a match, let it be included.
            if (state.labelFilters.indexOf(value) !== -1) {
                return false;
            }
        }
        // No matches, this actionplan is filtered
        return true;
    }
    return false;
}

function isFilteredByKeywords(state: ActionplanState, actionplan: Ticket) {
    if (state.keywords && state.keywords.trim()) {
        const keywords = state.keywords.split(" ")
            .map((v) => {return v.trim().toLowerCase();})
            .filter((v) => { return v.length > 0; });

        for (const keyword of keywords) {
            if (actionplan.dashboard_interpretation.toLowerCase().indexOf(keyword) > -1) { return false; }
            if (actionplan.ddd && actionplan.ddd.toString().toLowerCase().indexOf(keyword) > -1) { return false; }
            if (actionplan.objective && actionplan.objective.toString().toLowerCase().indexOf(keyword) > -1) { return false; }
        }
        return true;
    }
    return false;
}

function isIncluded(state: ActionplanState, actionplan: Ticket): boolean {
    return !isFilteredByDateRange(actionplan, state.dateRange)
        && !isFilteredByViewByFilters(state, actionplan)
        && !isFilteredByLabelGroups(state, actionplan)
        && !isFilteredByLabels(state, actionplan)
        && !isFilteredByKeywords(state, actionplan);
}

function sortViewByCounts(state: ActionplanState) {
    const sorted = Array.from(state.viewByCounts.values());

    if (!state.sortCategory) {
        sorted.sort((a: EntityCounts, b: EntityCounts) => {
            return a.name.localeCompare(b.name);
        });
    } else {
        const category = state.sortCategory || "";
        sorted.sort((a: EntityCounts, b: EntityCounts) => {
            return state.sortDirection === 1 ? b[category] - a[category] : a[category] - b[category];
        });
    }
    return sorted;
}

export function categorize(actionplan: Ticket) {
    const now = new Date();
    let parsedDate: Date | undefined = undefined;
    let response: string = categories.NO_DATE;

    if (actionplan.evaluation_date) {
        parsedDate = Utils.parseISOLocalDate(actionplan.evaluation_date);
    }

    if (actionplan.status === "closed" && actionplan.target_met === false) {
        response = categories.CLOSED_NOT_MET;

    } else if (actionplan.status === "closed" && actionplan.target_met === true) {
        response = categories.CLOSED_MET;

    } else if (actionplan.status !== "closed" && parsedDate && parsedDate < now) {
        response = categories.OPEN_LATE;

    } else if (actionplan.status === "open" && parsedDate && parsedDate >= now) {
        response = categories.OPEN_ON_TIME;
    }
    return response;
}

function categorizeWithGrandTotal(actionplan: Ticket) {
    return [categorize(actionplan), "grand-total"];
}

function isCategoryIncluded(state: ActionplanState, category: string) {
    if (state.includeAllStatus) { return true; }
    else if (state.includeClosedMet && category === categories.CLOSED_MET) { return true; }
    else if (state.includeClosedNotMet && category === categories.CLOSED_NOT_MET) { return true; }
    else if (state.includeOpenOnTime && category === categories.OPEN_ON_TIME) { return true; }
    else if (state.includeOpenLate && category === categories.OPEN_LATE) { return true; }
    else if (state.includeNoDate && category === categories.NO_DATE) { return true; }
    return false;
}

function iterActionplans(state: ActionplanState): void {
    state.viewByCounts = new Map<string, EntityCounts>();
    state.counts = newCountMap();

    state.minDate = new Date();
    state.maxDate = new Date();

    state.minFilteredDate = undefined;
    state.maxFilteredDate = undefined;

    state.filteredActionplans = [];
    state.maxCount = 0;

    for (const actionplan of state.allActionplans) {
        let parsedDate: Date | undefined = undefined;
        // Always check for min/max date for controls.
        if (actionplan.evaluation_date) {
            parsedDate = new Date(actionplan.evaluation_date as string);
            state.minDate = new Date(Math.min.apply(null, [state.minDate, parsedDate]));
            state.maxDate = new Date(Math.max.apply(null, [state.maxDate, parsedDate]));
        }

        if (!isIncluded(state, actionplan)) {
            continue;
        }

        const category = categorize(actionplan);
        state.counts[categories.GRAND_TOTAL] += 1;

        // Update totals without filtering by status.  We need to maintain separate counts
        if (category === categories.OPEN_LATE) { state.counts[categories.OPEN_LATE] += 1; }
        else if (category === categories.CLOSED_NOT_MET) { state.counts[categories.CLOSED_NOT_MET] += 1; }
        else if (category === categories.CLOSED_MET) { state.counts[categories.CLOSED_MET] += 1; }
        else if (category === categories.NO_DATE) { state.counts[categories.NO_DATE] += 1; }
        else if (category === categories.OPEN_ON_TIME) { state.counts[categories.OPEN_ON_TIME] += 1; }

        if (!isCategoryIncluded(state, category)) {
            continue;
        }

        state.filteredActionplans.push(actionplan);

        // Handle min/max filtered dates. This is for timeline domain.
        // Handle minimum filtered date - This should be the earliest created or evaluation date.
        // Handle maxmimum filtered date - This should be the latest evaluation date.
        const created = new Date(actionplan.created);
        state.minFilteredDate = state.minFilteredDate ? state.minFilteredDate : created;

        state.minFilteredDate = new Date(Math.min.apply(null, [state.minFilteredDate, created]));
        if (parsedDate !== undefined) {
            state.minFilteredDate = new Date(Math.min.apply(null, [state.minFilteredDate, parsedDate]));
            state.maxFilteredDate = state.maxFilteredDate ? state.maxFilteredDate : parsedDate;
            state.maxFilteredDate = new Date(Math.max.apply(null, [state.maxFilteredDate, parsedDate]));
        }

        const viewByKeys = viewByAccessors[state.viewBy](actionplan);

        for (const viewByKey of viewByKeys) {

            const viewByCounts = state?.viewByCounts?.get(viewByKey) || {
                name: viewByKey,
                ...newCountMap()
            };

            if (state.viewByCounts && !(viewByKey in state.viewByCounts)) {
                state.viewByCounts.set(viewByKey, viewByCounts);
            }

            viewByCounts[categories.GRAND_TOTAL] += 1;
            state.maxCount = Math.max(state.maxCount, viewByCounts[categories.GRAND_TOTAL]);

            if (category === categories.OPEN_LATE) {
                viewByCounts[categories.OPEN_LATE] += 1;

            } else if (category === categories.CLOSED_NOT_MET) {
                viewByCounts[categories.CLOSED_NOT_MET] += 1;

            } else if (category === categories.CLOSED_MET) {
                viewByCounts[categories.CLOSED_MET] += 1;

            } else if (category === categories.NO_DATE) {
                viewByCounts[categories.NO_DATE] += 1;

            } else if (category === categories.OPEN_ON_TIME) {
                viewByCounts[categories.OPEN_ON_TIME] += 1;
            }
        }
    }
    state.sortedViewByCounts = sortViewByCounts(state);

    // Ensure there is at least 30 days between min and max date.
    if (state.maxDate.getTime() - state.minDate.getTime() < minDayRange * msInDay) {
        state.maxDate = new Date(state.minDate.getTime() + (minDayRange * msInDay));
    }
}

// Use this to update the base, unfiltered set of actionplans.
export function setActionplans(state: ActionplanState, actionplans: Ticket[]) {
    const newState: ActionplanState = {...state};
    newState.allActionplans = actionplans;
    iterActionplans(newState);
    return newState;
}

export function setDateRange(state: ActionplanState, newDateRange: DateRange) {
    const newState: ActionplanState = {...state};
    newState.dateRange = newDateRange;
    iterActionplans(newState);
    return newState;
}

export function setFilters(state: ActionplanState, changes: FilterChanges) {
    const newState: ActionplanState = {...state};
    newState.viewBy = changes.viewBy;
    newState.viewByFilters = changes.viewByFilters;
    newState.labelGroupFilters = changes.labelGroupFilters;
    newState.labelFilters = changes.labelFilters;
    newState.keywords = changes.keywords;
    newState.focusedViewBy = undefined;
    newState.focusedCategory = undefined;
    newState.focusedActionplan = undefined;
    // Handle case were view by filters might have been set and we are changing the viewby.
    if (newState.viewBy !== state.viewBy) {
        newState.viewByFilters = undefined;
    }
    iterActionplans(newState);
    return newState;
}

export function setFocusedActionplan(state: ActionplanState, focusedActionplan: Ticket | undefined) {
    const newState: ActionplanState = {...state};
    newState.focusedActionplan = focusedActionplan;
    return newState;
}

export function setFocusedViewBy(state: ActionplanState, focusedCategory: string | undefined, focusedViewBy: string | undefined) {
    const newState: ActionplanState = {...state};
    newState.focusedCategory = focusedCategory;
    newState.focusedViewBy = focusedViewBy;
    return newState;
}

export function setSort(state: ActionplanState, sortCategory: string | undefined, sortDirection: number | undefined) {
    const newState = {...state};
    newState.sortCategory = sortCategory;
    newState.sortDirection = sortDirection || 0; // Default to ascending.
    newState.sortedViewByCounts = sortViewByCounts(newState);
    return newState;
}

export function setStatuses(state: ActionplanState, newStatuses: {[key: string]: boolean}) {
    const newState = {...state};
    newState.includeClosedMet = newStatuses[categories.CLOSED_MET];
    newState.includeClosedNotMet = newStatuses[categories.CLOSED_NOT_MET];
    newState.includeNoDate = newStatuses[categories.NO_DATE];
    newState.includeOpenLate = newStatuses[categories.OPEN_LATE];
    newState.includeOpenOnTime = newStatuses[categories.OPEN_ON_TIME];
    newState.includeAllStatus = (
        !newState.includeOpenOnTime &&
        !newState.includeOpenLate &&
        !newState.includeNoDate &&
        !newState.includeClosedNotMet &&
        !newState.includeClosedMet
    );
    iterActionplans(newState);
    return newState;
}

export function actionsForTable(state: ActionplanState) {
    if (state.focusedActionplan) {
        return [state.focusedActionplan];
    }
    else if (state.focusedCategory && state.focusedViewBy) {
        const g = new GroupedActionplans(state.filteredActionplans, [
            categorizeWithGrandTotal, viewByAccessors[state.viewBy]
        ]);
        return (g.grouped[state.focusedCategory] as GroupedActionplans).grouped[state.focusedViewBy] as Ticket[];
    }
    return state.filteredActionplans;
}

export class GroupedActionplans {
    // / Group actionplans by arbitrary accessor functions.
    grouped: {[key: string]: Array<Ticket> | GroupedActionplans};
    lengths: {[key: string]: number} = {};
    total: number;

    constructor (actionplans: Ticket[], accessors: Array<(a: Ticket) => string[] | string>) {
        this.total = 0;
        const accessor = accessors[0];
        const grouped: {[key: string]: Array<Ticket>} = {};
        actionplans.reduce((result, actionplan) => {
            let keys = accessor(actionplan);
            if (!Array.isArray(keys)) {
                keys = [keys];
            }
            for (const value of keys) {
                result[value] = result[value] || new Array<Ticket>;
                result[value].push(actionplan);
            }
            return result;
        }, grouped);

        // If this is the last accessor - we are in leaf nodes. No more nesting
        if (accessors.length === 1) {
            for (const key in grouped) {
                grouped[key].sort((a, b) => {return a.display_id - b.display_id;});
                this.lengths[key] = grouped[key].length;
                this.total += grouped[key].length;
            }
            this.grouped = grouped;

        // Otherwise handle the nesting
        } else {
            this.grouped = {};
            const reducedAccessors = accessors.slice(1, accessors.length);
            for (const key in grouped) {
                const group = new GroupedActionplans(grouped[key], reducedAccessors);
                this.grouped[key] = group;
                this.lengths[key] = group.total;
            }
        }
    }

    sorted() {
        const keys: Array<string> = [];
        for (const key in this.grouped) { keys.push(key); }
        keys.sort();
        return keys;
    }
}
